import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    MapContainer, ImageOverlay, Polygon,
    useMapEvents, useMap, Marker, Tooltip, Polyline
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { api } from "../../../lib/api";
import {
    ArrowLeft, Plus, Search, Trash2,
    Undo2, CheckCircle, XCircle, Pencil,
    MousePointer, Info, ChevronRight
} from "lucide-react";
import { toast } from "react-hot-toast";
import { useAdminT } from "../../../hooks/useAdminT";

import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

L.Marker.prototype.options.icon = L.icon({
    iconUrl: icon, shadowUrl: iconShadow,
    iconSize: [25, 41], iconAnchor: [12, 41]
});

const getImageDimensions = (src: string): Promise<{ w: number; h: number }> =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.width, h: img.height });
        img.onerror = reject;
        img.src = src;
    });

function MapController({ bounds }: { bounds: L.LatLngBoundsExpression }) {
    const map = useMap();
    useEffect(() => { if (bounds) map.fitBounds(bounds, { padding: [20, 20] }); }, [bounds, map]);
    return null;
}

function DrawingLayer({ points, setPoints, active, onComplete }: any) {
    useMapEvents({
        click(e) {
            if (!active) return;
            const newPt: [number, number] = [e.latlng.lat, e.latlng.lng];
            setPoints((prev: any[]) => {
                if (prev.length >= 3) {
                    const first = prev[0];
                    const dist = Math.hypot(newPt[0] - first[0], newPt[1] - first[1]);
                    const threshold = (e.target.getBounds().getNorth() - e.target.getBounds().getSouth()) * 0.02;
                    if (dist < threshold) { onComplete(); return prev; }
                }
                return [...prev, newPt];
            });
        },
        dblclick() {
            if (!active) return;
            onComplete();
        },
        contextmenu(e) {
            if (!active) return;
            e.originalEvent.preventDefault();
            setPoints((prev: any[]) => prev.slice(0, -1));
        }
    });

    if (points.length === 0) return null;

    return (
        <>
            {points.length >= 3 && (
                <Polygon
                    positions={points}
                    pathOptions={{ color: "#3b82f6", fillColor: "#3b82f6", fillOpacity: 0.2, weight: 2, dashArray: "6,4" }}
                />
            )}
            {points.length >= 2 && (
                <Polyline
                    positions={points}
                    pathOptions={{ color: "#3b82f6", weight: 2, dashArray: "6,4" }}
                />
            )}
            {points.map((p: any, idx: number) => (
                <Marker
                    key={idx}
                    position={p}
                    icon={L.divIcon({
                        className: '',
                        html: `<div style="
                            width:${idx === 0 ? 14 : 10}px;
                            height:${idx === 0 ? 14 : 10}px;
                            border-radius:50%;
                            background:${idx === 0 ? '#ef4444' : '#3b82f6'};
                            border:2px solid white;
                            box-shadow:0 1px 4px rgba(0,0,0,0.5);
                            cursor:${idx === 0 ? 'crosshair' : 'default'}
                        "></div>`,
                        iconSize: [idx === 0 ? 14 : 10, idx === 0 ? 14 : 10],
                        iconAnchor: [idx === 0 ? 7 : 5, idx === 0 ? 7 : 5]
                    })}
                >
                    <Tooltip direction="top" offset={[0, -8]}>
                        {idx === 0 ? "Start point — click to close" : `Point ${idx + 1}`}
                    </Tooltip>
                </Marker>
            ))}
        </>
    );
}

export default function FloorPlanEditorPage() {
    const { id, floorId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { t, isRtl } = useAdminT();

    const [imageBounds, setImageBounds] = useState<L.LatLngBoundsExpression | null>(null);
    const [selectedUnit, setSelectedUnit] = useState<any>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [drawPoints, setDrawPoints] = useState<any[]>([]);
    const [ghostShape, setGhostShape] = useState<any[] | null>(null);
    const [searchUnit, setSearchUnit] = useState("");
    const [sidebarOpen, setSidebarOpen] = useState(true);

    const { data: floor } = useQuery({
        queryKey: ["floor", floorId],
        queryFn: () => api.get(`/api/admin/floors/${floorId}`).then(r => r.data.data),
        enabled: !!floorId
    });

    const { data: units = [], isLoading: unitsLoading } = useQuery<any[]>({
        queryKey: ["project-units", id],
        queryFn: () => api.get(`/api/admin/units`, { params: { projectId: id, limit: 1000 } }).then(r => r.data.data),
    });

    useEffect(() => {
        if (floor?.image) {
            getImageDimensions(floor.image).then(({ w, h }) => setImageBounds([[0, 0], [h, w]]));
        }
    }, [floor]);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") cancelDraw();
            if ((e.ctrlKey || e.metaKey) && e.key === "z") {
                e.preventDefault();
                setDrawPoints(prev => prev.slice(0, -1));
            }
            if (e.key === "Enter" && isDrawing) finishDraw();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [isDrawing, drawPoints]);

    const updateUnitMutation = useMutation({
        mutationFn: (data: any) => api.put(`/api/admin/units/${data.id}`, {
            floorId, blueprintShape: data.shape
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["project-units", id] });
            toast.success(t("floorEditor.readyToSave"));
            cancelDraw();
        },
        onError: () => toast.error(t("floorEditor.toast.saveError"))
    });

    const cancelDraw = useCallback(() => {
        setIsDrawing(false);
        setDrawPoints([]);
        setGhostShape(null);
        setSelectedUnit(null);
    }, []);

    const startDraw = useCallback((unit: any) => {
        setSelectedUnit(unit);
        setIsDrawing(true);
        setDrawPoints([]);
        setGhostShape(unit.blueprintShape?.length > 0 ? unit.blueprintShape : null);
    }, []);

    const finishDraw = useCallback(() => {
        if (drawPoints.length < 3) { toast.error(t("floorEditor.toast.pointsError")); return; }
        if (selectedUnit) updateUnitMutation.mutate({ id: selectedUnit.id, shape: drawPoints });
    }, [drawPoints, selectedUnit]);

    const removeShape = (unit: any) => {
        if (!confirm(t("floorEditor.removeBoundary") + ` "${unit.slug}"?`)) return;
        api.put(`/api/admin/units/${unit.id}`, { floorId: null, blueprintShape: null })
            .then(() => {
                queryClient.invalidateQueries({ queryKey: ["project-units", id] });
                toast.success(t("floorEditor.removeBoundary") + " ✓");
            });
    };

    const assignedUnits = units.filter((u: any) => u.floorId === floorId && u.blueprintShape?.length > 0);

    const filteredUnits = (units as any[]).filter((u: any) =>
        u.slug?.toLowerCase().includes(searchUnit.toLowerCase()) ||
        u.translations?.some((t: any) => t.title?.toLowerCase().includes(searchUnit.toLowerCase()))
    );

    const pointsNeeded = Math.max(0, 3 - drawPoints.length);

    if (!floor || !imageBounds) return (
        <div className="h-screen flex items-center justify-center bg-slate-100">
            <div className="text-center">
                <div className="w-10 h-10 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-slate-500 text-sm">{t("floorEditor.loading")}</p>
            </div>
        </div>
    );

    return (
        <div className="flex flex-col h-screen bg-slate-900 overflow-hidden" dir={isRtl ? "rtl" : "ltr"}>

            {/* ══ Header */}
            <header className="flex-none bg-slate-800 text-white px-4 py-2.5 flex items-center gap-3 shadow-md z-30">
                <button
                    onClick={() => navigate(`/admin/projects/${id}/floors`)}
                    className="p-1.5 hover:bg-slate-700 rounded transition-colors"
                    title={t("floors.title")}
                >
                    <ArrowLeft size={18} className={isRtl ? "rotate-180" : ""} />
                </button>
                <div className="flex-1 min-w-0">
                    <h1 className="font-bold text-base leading-tight truncate">{floor.name}</h1>
                    <p className="text-xs text-slate-400">
                        {t("floorEditor.unitsMapped" as any, { count: assignedUnits.length })}
                    </p>
                </div>
                <button
                    onClick={() => setSidebarOpen(v => !v)}
                    className="p-1.5 hover:bg-slate-700 rounded transition-colors"
                    title={sidebarOpen ? t("nav.collapse") : t("floorEditor.searchUnits")}
                >
                    <ChevronRight size={18} className={`transition-transform ${sidebarOpen ? (isRtl ? '' : 'rotate-180') : (isRtl ? 'rotate-180' : '')}`} />
                </button>
            </header>

            {/* ══ Body */}
            <div className="flex flex-1 overflow-hidden">

                {/* ─ Sidebar */}
                <aside className={`flex-none bg-white flex flex-col overflow-hidden transition-all duration-300 border-r border-slate-200 shadow-lg z-20 ${sidebarOpen ? 'w-72 xl:w-80' : 'w-0'}`}>

                    {/* Create shortcut */}
                    <div className="px-3 pt-3 pb-2 border-b flex-none">
                        <button
                            onClick={() => window.open(`/admin/units?action=create&projectId=${id}`, '_blank')}
                            className="w-full py-2 bg-slate-800 text-white rounded-lg text-sm font-medium hover:bg-slate-700 transition flex items-center justify-center gap-2"
                        >
                            <Plus size={14} /> {t("floorEditor.createUnit")}
                        </button>
                        <p className="text-[10px] text-slate-400 mt-1 text-center">{t("common.opensNewTab")}</p>
                    </div>

                    {/* Search */}
                    <div className="px-3 py-2 border-b flex-none">
                        <div className="relative">
                            <Search size={13} className="absolute left-2.5 top-2.5 text-slate-400" />
                            <input
                                type="text"
                                placeholder={t("floorEditor.searchUnits")}
                                className="w-full pl-8 pr-2 py-2 text-sm border rounded-lg bg-slate-50 outline-none focus:border-slate-400 focus:bg-white transition"
                                value={searchUnit}
                                onChange={e => setSearchUnit(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Unit list */}
                    <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1.5">
                        {unitsLoading && (
                            <p className="text-center text-slate-400 text-sm py-6">{t("common.loading")}</p>
                        )}
                        {!unitsLoading && filteredUnits.length === 0 && (
                            <p className="text-center text-slate-400 text-sm py-6">{t("common.noResults")}</p>
                        )}
                        {filteredUnits.map((unit: any) => {
                            const isMapped = unit.floorId === floorId && unit.blueprintShape?.length > 0;
                            const isOtherFloor = unit.floorId && unit.floorId !== floorId;
                            const isActive = selectedUnit?.id === unit.id;

                            return (
                                <div
                                    key={unit.id}
                                    className={`group rounded-lg border p-2.5 transition-all select-none
                                        ${isActive ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-400' : ''}
                                        ${isMapped && !isActive ? 'border-emerald-200 bg-emerald-50/60' : ''}
                                        ${!isMapped && !isActive && !isOtherFloor ? 'border-slate-200 bg-white hover:border-slate-300' : ''}
                                        ${isOtherFloor ? 'border-slate-200 bg-white opacity-40' : ''}
                                    `}
                                >
                                    <div className="flex items-center gap-2">
                                        <div className={`w-2 h-2 rounded-full flex-none ${isMapped ? 'bg-emerald-500' : isOtherFloor ? 'bg-orange-400' : 'bg-slate-300'}`} />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-slate-800 truncate">{unit.slug}</div>
                                            <div className="text-xs text-slate-400">{unit.type} · {unit.area}m²</div>
                                        </div>
                                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                            {!isOtherFloor && !isDrawing && (
                                                <button
                                                    onClick={() => startDraw(unit)}
                                                    className={`p-1.5 rounded transition-colors ${isMapped ? 'text-blue-500 hover:bg-blue-100' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
                                                    title={isMapped ? t("floorEditor.redrawBoundary") : t("floorEditor.drawBoundary")}
                                                >
                                                    {isMapped ? <Pencil size={13} /> : <MousePointer size={13} />}
                                                </button>
                                            )}
                                            {isMapped && !isDrawing && (
                                                <button
                                                    onClick={() => removeShape(unit)}
                                                    className="p-1.5 rounded text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                                    title={t("floorEditor.removeBoundary")}
                                                >
                                                    <Trash2 size={13} />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {!isOtherFloor && !isDrawing && (
                                        <button
                                            onClick={() => startDraw(unit)}
                                            className={`mt-2 w-full text-xs py-1.5 rounded-md border transition-all font-medium
                                                ${isMapped
                                                    ? 'border-blue-200 text-blue-600 hover:bg-blue-50'
                                                    : 'border-slate-200 text-slate-500 hover:bg-slate-50 hover:border-slate-300'
                                                }
                                            `}
                                        >
                                            {isMapped ? `✏️ ${t("floorEditor.redrawBoundary")}` : `＋ ${t("floorEditor.drawBoundary")}`}
                                        </button>
                                    )}
                                    {isOtherFloor && (
                                        <p className="mt-1 text-[10px] text-orange-500">{t("floorEditor.otherFloorHint")}</p>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Legend */}
                    <div className="flex-none border-t px-3 py-2 bg-slate-50 space-y-1">
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{t("floorEditor.legend")}</p>
                        <div className="flex gap-3 text-[11px] text-slate-500">
                            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-full" /> {t("floorEditor.mapped")}</span>
                            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-slate-300 rounded-full" /> {t("floorEditor.unmapped")}</span>
                            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-orange-400 rounded-full" /> {t("floorEditor.otherFloor")}</span>
                        </div>
                    </div>
                </aside>

                {/* ─ Map */}
                <div className="flex-1 relative overflow-hidden">
                    <MapContainer
                        crs={L.CRS.Simple}
                        bounds={imageBounds}
                        className="h-full w-full"
                        minZoom={-3}
                        maxZoom={3}
                        scrollWheelZoom={true}
                        doubleClickZoom={false}
                    >
                        <ImageOverlay url={floor.image} bounds={imageBounds} />

                        {/* Ghost: old boundary reference when redrawing */}
                        {isDrawing && ghostShape && (
                            <Polygon
                                positions={ghostShape}
                                pathOptions={{
                                    color: "#f59e0b", fillColor: "#f59e0b",
                                    fillOpacity: 0.08, weight: 1.5,
                                    dashArray: "8,6", opacity: 0.5
                                }}
                            >
                                <Tooltip permanent direction="center" className="text-[10px] bg-amber-50 border-amber-300 text-amber-700 shadow-sm">
                                    {t("floorEditor.previousBoundary")}
                                </Tooltip>
                            </Polygon>
                        )}

                        {/* Existing unit polygons */}
                        {assignedUnits
                            .filter((u: any) => u.id !== selectedUnit?.id)
                            .map((unit: any) => (
                                <Polygon
                                    key={unit.id}
                                    positions={unit.blueprintShape}
                                    pathOptions={{
                                        color: "#10b981",
                                        fillColor: "#10b981",
                                        fillOpacity: 0.25,
                                        weight: 2
                                    }}
                                    eventHandlers={{
                                        click: () => { if (!isDrawing) startDraw(unit); }
                                    }}
                                >
                                    <Tooltip sticky className="text-xs">
                                        <strong>{unit.slug}</strong><br />
                                        {unit.type} · {unit.area}m²
                                    </Tooltip>
                                </Polygon>
                            ))
                        }

                        <DrawingLayer
                            points={drawPoints}
                            setPoints={setDrawPoints}
                            active={isDrawing}
                            onComplete={finishDraw}
                        />

                        <MapController bounds={imageBounds} />
                    </MapContainer>

                    {/* ── DRAWING TOOLBAR (floating) */}
                    {isDrawing && (
                        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[400] pointer-events-auto">
                            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden min-w-[340px]">
                                {/* Unit label */}
                                <div className="bg-blue-600 text-white text-xs font-semibold px-4 py-2 flex items-center justify-between">
                                    <span>✏️ {t("floorEditor.drawingMode", { unit: selectedUnit?.slug })}</span>
                                    <span className="bg-blue-500 px-2 py-0.5 rounded-full font-mono">
                                        {t("floorEditor.pointsPlaced", { count: drawPoints.length })}
                                    </span>
                                </div>

                                {/* Action buttons */}
                                <div className="flex divide-x divide-slate-100">
                                    <button
                                        onClick={() => setDrawPoints(prev => prev.slice(0, -1))}
                                        disabled={drawPoints.length === 0}
                                        className="flex-1 flex flex-col items-center gap-1 py-3 px-2 hover:bg-slate-50 transition disabled:opacity-30 disabled:cursor-not-allowed group"
                                        title={t("floorEditor.undo")}
                                    >
                                        <Undo2 size={18} className="text-slate-600 group-hover:text-slate-800" />
                                        <span className="text-[10px] text-slate-500">{t("floorEditor.undo")}</span>
                                        <span className="text-[9px] font-mono bg-slate-100 text-slate-400 px-1.5 rounded">Ctrl+Z</span>
                                    </button>

                                    <button
                                        onClick={cancelDraw}
                                        className="flex-1 flex flex-col items-center gap-1 py-3 px-2 hover:bg-red-50 transition group"
                                        title={t("floorEditor.cancel")}
                                    >
                                        <XCircle size={18} className="text-red-400 group-hover:text-red-600" />
                                        <span className="text-[10px] text-slate-500">{t("floorEditor.cancel")}</span>
                                        <span className="text-[9px] font-mono bg-slate-100 text-slate-400 px-1.5 rounded">Esc</span>
                                    </button>

                                    <button
                                        onClick={finishDraw}
                                        disabled={drawPoints.length < 3 || updateUnitMutation.isPending}
                                        className="flex-1 flex flex-col items-center gap-1 py-3 px-2 bg-emerald-50 hover:bg-emerald-100 transition disabled:opacity-30 disabled:cursor-not-allowed group"
                                        title={t("floorEditor.save")}
                                    >
                                        <CheckCircle size={18} className="text-emerald-500 group-hover:text-emerald-700" />
                                        <span className="text-[10px] text-slate-500">
                                            {updateUnitMutation.isPending ? t("floorEditor.saving") : t("floorEditor.save")}
                                        </span>
                                        <span className="text-[9px] font-mono bg-slate-100 text-slate-400 px-1.5 rounded">Enter</span>
                                    </button>
                                </div>

                                {/* Hint bar */}
                                <div className="bg-slate-50 border-t text-[10px] text-slate-400 text-center px-3 py-1.5 flex items-center justify-center gap-2">
                                    <Info size={10} />
                                    {drawPoints.length < 3
                                        ? t("floorEditor.addMorePoints", { count: pointsNeeded })
                                        : t("floorEditor.readyToSave")
                                    }
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── IDLE hint */}
                    {!isDrawing && (
                        <div className="absolute bottom-4 right-4 z-[400] pointer-events-none">
                            <div className="bg-white/90 backdrop-blur text-[11px] text-slate-500 px-3 py-2 rounded-lg shadow border border-slate-200 max-w-[200px]">
                                <p className="font-medium text-slate-700 mb-0.5">💡 {t("floorEditor.statusHint")}</p>
                                <p>{t("floorEditor.idleTip")}</p>
                            </div>
                        </div>
                    )}

                    {/* Drawing mode top indicator */}
                    {isDrawing && (
                        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[400] pointer-events-none">
                            <div className="bg-blue-600/90 text-white text-xs font-medium px-4 py-1.5 rounded-full shadow-lg backdrop-blur-sm">
                                ✏️ {t("floorEditor.drawingActive")} —{" "}
                                {drawPoints.length < 3
                                    ? t("floorEditor.addMorePoints", { count: pointsNeeded })
                                    : t("floorEditor.readyToSave")
                                }
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
